/**
 * 统一的后端请求封装。
 *
 * 之前 `api/actions.ts` 与 `api/finance.ts` 各有一份完全相同的 request 实现；
 * 现在只保留这一份，调用方从 `../lib/http` 引入。
 *
 * - 根地址：设置 VITE_API_BASE_URL 时指向远端，未设置则留空走相对路径
 *   （本地由 Vite proxy 转到 127.0.0.1:8934）。
 * - 自动附带 Supabase 的 `Authorization` 头。
 * - 401 时先静默刷新 token 并重试原请求一次；仍 401 才跳登录页。
 * - 跳登录前暂存原路由，登录成功后回跳（见 auth/postLoginRedirect）。
 * - 非 2xx 抛出后端 `detail` 文案。
 */

import { authHeader, getSupabase } from '../auth/supabase'
import { savePostLoginRedirect } from '../auth/postLoginRedirect'

export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

// 并发 401 共用同一个刷新 promise，避免同时打多次 refreshSession
let refreshPromise: Promise<boolean> | null = null

function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  const client = getSupabase()
  if (!client) return Promise.resolve(false)
  refreshPromise = client
    .refreshSession()
    .then(({ data, error }) => Boolean(!error && data.session?.access_token))
    .catch(() => false)
    .finally(() => {
      refreshPromise = null
    })
  return refreshPromise
}

function redirectToLogin(): void {
  // 已在登录页不重复跳转，也不覆盖已暂存的原路由
  if (window.location.hash.includes('#/login')) return
  savePostLoginRedirect()
  window.location.hash = '#/login'
}

function send(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
      ...(init?.headers ?? {}),
    },
  })
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response = await send(path, init)
  if (response.status === 401) {
    // refresh 成功则用新 token 重试原请求一次；失败/未配置直接走登录
    if (await refreshAccessToken()) {
      response = await send(path, init)
    }
    if (response.status === 401) {
      redirectToLogin()
      throw new Error('未登录')
    }
  }
  const text = await response.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = text
    }
  }
  if (!response.ok) {
    const detail = typeof payload === 'object' && payload !== null && 'detail' in payload
      ? String((payload as { detail: unknown }).detail)
      : `HTTP ${response.status}`
    throw new Error(detail)
  }
  return payload as T
}
